import { useEffect, useMemo, useRef, useState } from 'react';
import { Link, Navigate, useNavigate, useParams } from 'react-router';
import { useForm, useWatch } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import {
  createWorkOrderStockIssue,
  fetchWorkOrderStockIssueCandidates,
  fetchWorkOrderStockIssueDetail,
  updateWorkOrderStockIssue,
} from '#/api/momMgmt/workOrder/outbound';
import { fetchWorkOrderDetail } from '#/api/momMgmt/workOrder/fetchDetail';
import { ROUTES } from '#/config/routes';
import { useFormValidationSummary } from '#/hooks/useFormValidationSummary';
import { DocumentStatusBadge } from '#/modules/commonComponents/badge/DocumentStatusBadge';
import { Button } from '#/modules/commonComponents/button';
import FormValidationSummary from '#/modules/commonComponents/form/FormValidationSummary';
import ColumnTipHeader from '#/modules/commonComponents/table/ColumnTipHeader';
import PageShell from '#/modules/infra/layout/PageShell';
import { canEditStockIssue } from '#/modules/imsMgmt/receipts/stockIssue/helper/issueStatus';
import { getWorkOrderApiErrorMessage } from '#/modules/momMgmt/workOrder/helper/apiError';
import { WORK_ORDER_STATUS } from '#/modules/momMgmt/workOrder/helper/workOrderStatus';
import { toast } from '#/utils/toast';
import {
  workOrderDetailQueryKey,
  workOrderStockIssueCandidatesQueryKey,
  stockIssueDetailQueryKey,
} from '#/utils/queryKeys';

import WorkOrderOutboundDraftPanel from '../components/WorkOrderOutboundDraftPanel';
import WorkOrderOutboundMaterialList from '../components/WorkOrderOutboundMaterialList';
import WorkOrderOutboundSelectedPanel from '../components/WorkOrderOutboundSelectedPanel';
import WorkOrderOutboundSourcePickerDialog from '../components/WorkOrderOutboundSourcePickerDialog';
import { mapOutboundFormToCreatePayload } from '../helper/mapFormToCreatePayload';
import {
  mapStockIssueDetailToOutboundForm,
  mergeOutboundLinesAvailableQty,
} from '../helper/mapStockIssueDetailToForm';
import {
  buildOutboundDraftLine,
  normalizeStockIssueCandidates,
} from '../helper/normalizeStockIssueCandidates';
import {
  invalidateWorkOrderOutboundQueries,
} from '../helper/invalidateWorkOrderOutboundQueries';
import {
  DEFAULT_OUTBOUND_FORM_VALUES,
  WORK_ORDER_OUTBOUND_FIELD_LABELS,
  workOrderOutboundFormSchema,
} from '../schemas/outboundSchema';

function pickUpdatedAt(detailLike) {
  return detailLike?.record?.updatedAt ?? detailLike?.updatedAt ?? null;
}

const CreateEditWorkOrderOutboundPage = () => {
  const { id: workOrderId, issueId } = useParams();
  const isEditMode = Boolean(issueId);
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const issueUpdatedAtRef = useRef(null);
  const hydratedIssueIdRef = useRef(null);
  const availableMergedRef = useRef(false);

  const [pickerOpen, setPickerOpen] = useState(false);
  const [pickerMaterial, setPickerMaterial] = useState(null);
  const [focusedMtrId, setFocusedMtrId] = useState(null);
  const [focusToken, setFocusToken] = useState(0);
  const [collapsedByMtrId, setCollapsedByMtrId] = useState({});

  const {
    isSubmitAttempted,
    markSubmitAttempted,
    resetValidationSummary,
  } = useFormValidationSummary();

  const workOrderQuery = useQuery({
    queryKey: workOrderDetailQueryKey(workOrderId),
    queryFn: () => fetchWorkOrderDetail(workOrderId),
    enabled: Boolean(workOrderId),
  });

  const issueQuery = useQuery({
    queryKey: stockIssueDetailQueryKey(issueId),
    queryFn: () => fetchWorkOrderStockIssueDetail(workOrderId, issueId),
    enabled: Boolean(workOrderId && issueId),
  });

  const {
    register,
    control,
    handleSubmit,
    setValue,
    getValues,
    reset,
    formState: { errors },
  } = useForm({
    resolver: zodResolver(workOrderOutboundFormSchema),
    defaultValues: DEFAULT_OUTBOUND_FORM_VALUES,
  });

  const lines = useWatch({ control, name: 'lines', defaultValue: [] });
  const workOrder = workOrderQuery.data?.data;
  const issueDetail = issueQuery.data?.data;
  const canOperate = workOrder?.status === WORK_ORDER_STATUS.PROCESSING;

  const candidatesQuery = useQuery({
    queryKey: workOrderStockIssueCandidatesQueryKey(workOrderId),
    queryFn: () => fetchWorkOrderStockIssueCandidates(workOrderId),
    enabled: Boolean(workOrderId) && canOperate,
    retry: false,
  });

  const candidates = useMemo(() => {
    if (!canOperate || candidatesQuery.isLoading || candidatesQuery.isError) {
      return { materials: [], workWarehouse: null, isEmpty: true };
    }
    return normalizeStockIssueCandidates(candidatesQuery.data);
  }, [
    canOperate,
    candidatesQuery.data,
    candidatesQuery.isError,
    candidatesQuery.isLoading,
  ]);

  const workWarehouseId = candidates.workWarehouse?.id
    ?? workOrder?.workWarehouse?.id
    ?? null;

  useEffect(() => {
    if (!workOrder) {
      return;
    }
    if (isEditMode && !issueDetail) {
      return;
    }

    if (isEditMode) {
      if (hydratedIssueIdRef.current === issueId) {
        return;
      }
      hydratedIssueIdRef.current = issueId;
      availableMergedRef.current = false;
      issueUpdatedAtRef.current = pickUpdatedAt(issueDetail);
      resetValidationSummary();
      reset(mapStockIssueDetailToOutboundForm(issueDetail));
      return;
    }

    hydratedIssueIdRef.current = null;
    availableMergedRef.current = false;
    issueUpdatedAtRef.current = null;
    resetValidationSummary();
    reset(DEFAULT_OUTBOUND_FORM_VALUES);
  }, [
    workOrder,
    issueDetail,
    isEditMode,
    issueId,
    reset,
    resetValidationSummary,
  ]);

  useEffect(() => {
    if (!isEditMode || availableMergedRef.current || !candidates.materials?.length) {
      return;
    }
    const currentLines = getValues('lines') ?? [];
    if (currentLines.length === 0) {
      return;
    }
    availableMergedRef.current = true;
    const merged = mergeOutboundLinesAvailableQty(currentLines, candidates.materials);
    setValue('lines', merged, { shouldDirty: false, shouldValidate: true });
  }, [isEditMode, candidates.materials, getValues, setValue]);

  const selectedCountByMtrId = useMemo(() => {
    const map = {};
    (lines ?? []).forEach((line) => {
      if (!line?.mtrId) {
        return;
      }
      map[line.mtrId] = (map[line.mtrId] || 0) + 1;
    });
    return map;
  }, [lines]);

  const pickerInitialSourceKeys = useMemo(() => {
    if (!pickerMaterial?.mtrId) {
      return [];
    }
    return (lines ?? [])
      .filter((line) => line.mtrId === pickerMaterial.mtrId)
      .map((line) => line.sourceKey)
      .filter(Boolean);
  }, [lines, pickerMaterial]);

  const saveMutation = useMutation({
    mutationFn: async (values) => {
      const payload = mapOutboundFormToCreatePayload(values);
      if (isEditMode) {
        return updateWorkOrderStockIssue(workOrderId, issueId, {
          ...payload,
          updatedAt: issueUpdatedAtRef.current,
        });
      }
      return createWorkOrderStockIssue(workOrderId, payload);
    },
    onSuccess: async (result) => {
      toast.success(result?.message || (isEditMode ? '出庫單已更新' : '出庫單已建立'));
      await invalidateWorkOrderOutboundQueries(queryClient, workOrderId);

      if (!isEditMode) {
        const createdId = result?.data?.id;
        if (createdId) {
          navigate(ROUTES.workOrderOutboundEdit(workOrderId, createdId), { replace: true });
          return;
        }
        navigate(ROUTES.workOrderOutbound(workOrderId));
        return;
      }

      issueUpdatedAtRef.current = pickUpdatedAt(result?.data) ?? issueUpdatedAtRef.current;
      await queryClient.invalidateQueries({ queryKey: stockIssueDetailQueryKey(issueId) });
      hydratedIssueIdRef.current = null;
      await issueQuery.refetch();
    },
    onError: (error) => {
      toast.error(getWorkOrderApiErrorMessage(
        error,
        isEditMode ? '更新出庫單失敗' : '出庫失敗',
      ));
    },
  });

  function handleFocusMaterial(mtrId) {
    if (!mtrId) {
      return;
    }
    setFocusedMtrId(mtrId);
    setFocusToken((prev) => prev + 1);
    setCollapsedByMtrId((prev) => ({ ...prev, [mtrId]: false }));
  }

  function handleToggleGroup(mtrId) {
    if (!mtrId) {
      return;
    }
    setCollapsedByMtrId((prev) => ({
      ...prev,
      [mtrId]: prev[mtrId] !== true,
    }));
  }

  function handleAfterRemoveLine(mtrId) {
    if (mtrId && focusedMtrId === mtrId) {
      setFocusedMtrId(null);
    }
  }

  function handleOpenSourcePicker(material) {
    if (formDisabled || saveMutation.isPending) {
      return;
    }
    if (!material?.sources?.length) {
      return;
    }
    setPickerMaterial(material);
    setPickerOpen(true);
  }

  function syncMaterialSources(selectedSources) {
    const material = pickerMaterial;
    if (!material?.mtrId) {
      return;
    }

    const currentLines = getValues('lines') ?? [];
    const otherLines = currentLines.filter((line) => line.mtrId !== material.mtrId);
    const existingByKey = {};
    currentLines.forEach((line) => {
      if (line.mtrId === material.mtrId && line.sourceKey) {
        existingByKey[line.sourceKey] = line;
      }
    });

    const nextMaterialLines = selectedSources.map((source) => {
      const existing = existingByKey[source.sourceKey];
      if (existing) {
        return existing;
      }
      return buildOutboundDraftLine(material, source, source.qty);
    });

    setValue('lines', [...otherLines, ...nextMaterialLines], {
      shouldDirty: true,
      shouldValidate: true,
    });

    if (nextMaterialLines.length > 0) {
      handleFocusMaterial(material.mtrId);
    } else if (focusedMtrId === material.mtrId) {
      setFocusedMtrId(null);
    }
  }

  function handleInvalidSubmit() {
    markSubmitAttempted();
  }

  function onSubmit(values) {
    markSubmitAttempted();
    if (!canOperate) {
      toast.error('僅生產中工單可出庫');
      return;
    }
    if (isEditMode && !canEditStockIssue(issueDetail?.status)) {
      toast.error('僅草稿出庫單可編輯');
      return;
    }
    resetValidationSummary();
    saveMutation.mutate(values);
  }

  const isPageLoading = workOrderQuery.isLoading
    || (isEditMode && issueQuery.isLoading);
  const isPageError = workOrderQuery.isError
    || (isEditMode && issueQuery.isError);

  if (!isPageLoading && workOrder && workOrder.status !== WORK_ORDER_STATUS.PROCESSING) {
    return <Navigate to={ROUTES.workOrderDetail(workOrderId)} replace />;
  }

  if (!isPageLoading && isEditMode && issueDetail
    && !canEditStockIssue(issueDetail.status)) {
    return <Navigate to={ROUTES.workOrderOutbound(workOrderId)} replace />;
  }

  const formDisabled = !canOperate
    || (isEditMode && !canEditStockIssue(issueDetail?.status));
  const busy = formDisabled || saveMutation.isPending;
  const pageTitle = isEditMode ? '編輯出庫單' : '新建出庫單';

  let candidatesEmptyHint = '尚無可出庫候選物料。';
  if (!canOperate) {
    candidatesEmptyHint = '僅生產中工單可載入出庫候選。';
  } else if (candidatesQuery.isError) {
    candidatesEmptyHint = getWorkOrderApiErrorMessage(
      candidatesQuery.error,
      '載入出庫候選失敗',
    );
  }

  return (
    <PageShell variant="formTall">
      <div className="layout-shell__body">
        <div className="mx-auto w-full max-w-6xl">
          <div className="create_edit_page__header">
            <h1 className="page_title mb-0! flex flex-wrap items-center gap-2">
              <span className="border-l-5 border-primary-400 pl-2.5">{pageTitle}</span>
              {isEditMode && issueDetail?.status ? (
                <DocumentStatusBadge status={issueDetail.status} size="sm" />
              ) : null}
            </h1>
            <div className="flex flex-wrap items-center gap-2">
              <Button type="button" variant="outline" asChild>
                <Link to={ROUTES.workOrderOutbound(workOrderId)}>返回詳情</Link>
              </Button>
            </div>
          </div>

          {isPageLoading ? (
            <p className="text-sm text-muted-foreground">載入中…</p>
          ) : null}

          {isPageError ? (
            <p className="text-sm text-destructive">
              {getWorkOrderApiErrorMessage(
                workOrderQuery.error || issueQuery.error,
                '找不到出庫單資料',
              )}
            </p>
          ) : null}

          {!isPageLoading && !isPageError && workOrder ? (
            <>
              <p className="mb-3 text-sm text-slate-600">
                {`工單 ${workOrder.workOrderNo || '—'}`}
                {isEditMode && issueDetail?.issueNo
                  ? ` ／ 出庫單 ${issueDetail.issueNo}`
                  : ''}
              </p>

              <form
                className="sheet space-y-4 py-5"
                onSubmit={handleSubmit(onSubmit, handleInvalidSubmit)}
                noValidate
              >
                {!canOperate ? (
                  <p className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-900">
                    僅
                    <span className="font-medium">生產中（PROCESSING）</span>
                    工單可操作出庫。
                  </p>
                ) : null}

                <FormValidationSummary
                  show={isSubmitAttempted}
                  errors={errors}
                  fieldLabels={WORK_ORDER_OUTBOUND_FIELD_LABELS}
                />

                <WorkOrderOutboundDraftPanel
                  control={control}
                  register={register}
                  errors={errors}
                  disabled={busy}
                />

                <div
                  className="
                    grid grid-cols-1 gap-4
                    @4xl/sheet:grid-cols-2
                  "
                >
                  <section className="form-section min-w-0">
                    <h3 className="form-subsection__title">
                      <ColumnTipHeader
                        label="可選物料"
                        tip="點選列可在右側查看已加入來源"
                      />
                    </h3>
                    <div className="mt-3 stock-ops-panel-scroll">
                      {canOperate && candidatesQuery.isLoading ? (
                        <p className="text-sm text-muted-foreground">載入出庫候選…</p>
                      ) : (
                        <WorkOrderOutboundMaterialList
                          materials={candidates.materials}
                          selectedCountByMtrId={selectedCountByMtrId}
                          focusedMtrId={focusedMtrId}
                          focusToken={focusToken}
                          workWarehouseId={workWarehouseId}
                          onOpenSourcePicker={handleOpenSourcePicker}
                          onFocusMaterial={handleFocusMaterial}
                          emptyHint={candidatesEmptyHint}
                          disabled={busy}
                        />
                      )}
                    </div>
                  </section>

                  <section className="form-section min-w-0">
                    <h3 className="form-subsection__title">
                      <ColumnTipHeader
                        label="已選取"
                        tip="點標題列會展開／收合；點子項目會對應左側的可選物料"
                      />
                    </h3>
                    <div className="mt-3 stock-ops-panel-scroll">
                      <WorkOrderOutboundSelectedPanel
                        control={control}
                        register={register}
                        errors={errors}
                        setValue={setValue}
                        disabled={busy}
                        focusedMtrId={focusedMtrId}
                        focusToken={focusToken}
                        onFocusMaterial={handleFocusMaterial}
                        collapsedByMtrId={collapsedByMtrId}
                        onToggleGroup={handleToggleGroup}
                        onAfterRemoveLine={handleAfterRemoveLine}
                      />
                    </div>
                  </section>
                </div>

                <div className="create_edit_form__actions stock-ops-actions mt-4 flex flex-wrap justify-end gap-2">
                  <Button type="button" variant="outline" asChild>
                    <Link to={ROUTES.workOrderOutbound(workOrderId)}>取消</Link>
                  </Button>
                  <Button type="submit" disabled={busy}>
                    {saveMutation.isPending ? '儲存中…' : '儲存'}
                  </Button>
                </div>

                <WorkOrderOutboundSourcePickerDialog
                  key={pickerOpen
                    ? `${pickerMaterial?.mtrId}-${pickerInitialSourceKeys.slice().sort().join('|')}`
                    : 'closed'}
                  open={pickerOpen}
                  onOpenChange={setPickerOpen}
                  material={pickerMaterial}
                  initialSourceKeys={pickerInitialSourceKeys}
                  onConfirm={syncMaterialSources}
                />
              </form>
            </>
          ) : null}
        </div>
      </div>
    </PageShell>
  );
};

export default CreateEditWorkOrderOutboundPage;
